import { Box3, Group, Mesh, type Object3D, Vector3 } from "three";
import { type ModelAssetLoader, modelAssetLibrary } from "../models/model-asset-library";
import { ModelAsset } from "../models/model-assets";
import { disposeObject3DResources as disposeMeshes } from "../models/shared-model-resources";
import { RackPostDirection } from "./rack-post";

type RackPostColumnPosition = {
  x?: number;
  y?: number;
  z?: number;
};

export type RackPostColumnOptions = {
  crossbarYPositions?: number[];
  crossbarZOffset?: number;
  direction?: RackPostDirection;
  heightCellCount?: number;
  position?: RackPostColumnPosition;
  postBaseY?: number;
  postBodyHeight?: number;
  postWidth?: number;
  spanLength?: number;
};

type Crossbar = {
  height: number;
  object: Group;
  originalLength: number;
};

const frontCrossbarRotationY = -Math.PI / 2;
const defaultCrossbarZOffset = 0.0185;

export class RackPostColumn extends Group {
  private readonly contentRoot = new Group();
  private crossbars: Crossbar[] = [];
  private crossbarYPositions?: number[];
  private crossbarZOffset: number;
  private direction: RackPostDirection;
  private heightCellCount: number;
  private postBaseY: number;
  private postBodyHeight: number;
  private postWidth: number;
  private spanLength: number;

  constructor({
    crossbarYPositions,
    crossbarZOffset = defaultCrossbarZOffset,
    direction = RackPostDirection.Front,
    heightCellCount = 1,
    position = {},
    postBaseY = 0,
    postBodyHeight = 1,
    postWidth = 0,
    spanLength = 1
  }: RackPostColumnOptions = {}) {
    super();
    this.crossbarYPositions = crossbarYPositions;
    this.crossbarZOffset = crossbarZOffset;
    this.direction = direction;
    this.heightCellCount = heightCellCount;
    this.postBaseY = postBaseY;
    this.postBodyHeight = postBodyHeight;
    this.postWidth = postWidth;
    this.spanLength = spanLength;
    this.name = "rack-post-column";
    this.position.set(position.x ?? 0, position.y ?? 0, position.z ?? 0);
    this.add(this.contentRoot);
    this.applyContentRootTransform();
  }

  static async create(
    options: RackPostColumnOptions = {},
    loader: ModelAssetLoader = modelAssetLibrary
  ) {
    const column = new RackPostColumn(options);
    await column.load(loader);
    return column;
  }

  setColumnPosition(position: RackPostColumnPosition) {
    this.position.set(
      position.x ?? this.position.x,
      position.y ?? this.position.y,
      position.z ?? this.position.z
    );
  }

  setCrossbarZOffset(crossbarZOffset: number) {
    this.crossbarZOffset = crossbarZOffset;
    this.updateCrossbarTransforms();
  }

  setCrossbarYPositions(
    crossbarYPositions: number[],
    loader: ModelAssetLoader = modelAssetLibrary
  ) {
    this.crossbarYPositions = [...crossbarYPositions];
    return this.reloadCrossbars(loader);
  }

  setDirection(direction: RackPostDirection) {
    this.direction = direction;
    this.applyContentRootTransform();
  }

  setHeightCellCount(heightCellCount: number, loader: ModelAssetLoader = modelAssetLibrary) {
    this.heightCellCount = heightCellCount;
    return this.reloadCrossbars(loader);
  }

  setHeightMetrics(postBaseY: number, postBodyHeight: number) {
    this.postBaseY = postBaseY;
    this.postBodyHeight = postBodyHeight;
    this.updateCrossbarTransforms();
  }

  setPostWidth(postWidth: number) {
    this.postWidth = postWidth;
    this.updateCrossbarTransforms();
  }

  setSpanLength(spanLength: number) {
    this.spanLength = spanLength;
    this.applyContentRootTransform();
    this.updateCrossbarTransforms();
  }

  private async load(loader: ModelAssetLoader) {
    await this.reloadCrossbars(loader);
  }

  private async reloadCrossbars(loader: ModelAssetLoader) {
    this.crossbars.forEach((crossbar) => {
      this.contentRoot.remove(crossbar.object);
      disposeMeshes(crossbar.object);
    });
    this.crossbars = await Promise.all(
      Array.from({ length: this.getCrossbarCount() }, (_, index) => loadCrossbar(loader, index))
    );
    this.contentRoot.add(...this.crossbars.map((crossbar) => crossbar.object));
    this.updateCrossbarTransforms();
  }

  private updateCrossbarTransforms() {
    const targetLength = Math.max(this.spanLength - this.postWidth, 0);

    this.getCrossbarYPositions().forEach((targetY, index) => {
      const crossbar = this.crossbars[index];

      if (!crossbar) {
        return;
      }

      crossbar.object.scale.x = targetLength / crossbar.originalLength;
      crossbar.object.position.set(0, targetY - crossbar.height / 2, this.crossbarZOffset);
    });
  }

  private applyContentRootTransform() {
    this.contentRoot.position.x = this.spanLength / 2;
    this.contentRoot.rotation.y = this.direction === RackPostDirection.Back ? Math.PI : 0;
  }

  private getCrossbarCount() {
    if (this.crossbarYPositions) {
      return this.crossbarYPositions.length;
    }
    return Math.max(this.heightCellCount - 1, 0);
  }

  private getCrossbarYPositions() {
    if (this.crossbarYPositions) {
      return this.crossbarYPositions;
    }
    const cellHeight = this.postBodyHeight / this.heightCellCount;

    return Array.from(
      { length: this.getCrossbarCount() },
      (_, index) => this.postBaseY + cellHeight * (index + 1)
    );
  }
}

export async function measureRackCrossbarHeight(loader: ModelAssetLoader = modelAssetLibrary) {
  const crossbar = await loadCrossbar(loader, 0);
  const height = crossbar.height;
  disposeMeshes(crossbar.object);
  return height;
}

async function loadCrossbar(loader: ModelAssetLoader, index: number) {
  const content = await loader.load(ModelAsset.Crossbar);

  content.rotation.y = frontCrossbarRotationY;
  enableShadows(content);

  const crossbar = createBottomOriginGroup(content).object;
  crossbar.name = `crossbar-${index}`;

  return {
    height: getObjectHeight(crossbar),
    object: crossbar,
    originalLength: getObjectWidth(crossbar)
  };
}

function enableShadows(object: Object3D) {
  object.traverse((child) => {
    if (child instanceof Mesh) {
      child.castShadow = true;
      child.receiveShadow = true;
    }
  });
}

function createBottomOriginGroup(content: Object3D) {
  content.updateWorldMatrix(true, true);

  const bounds = new Box3().setFromObject(content);
  const center = bounds.getCenter(new Vector3());
  const group = new Group();

  content.position.x -= center.x;
  content.position.y -= bounds.min.y;
  content.position.z -= center.z;
  group.add(content);
  group.updateWorldMatrix(true, true);

  return {
    object: group
  };
}

function getObjectHeight(object: Object3D) {
  object.updateWorldMatrix(true, true);
  return new Box3().setFromObject(object).getSize(new Vector3()).y;
}

function getObjectWidth(object: Object3D) {
  object.updateWorldMatrix(true, true);
  return new Box3().setFromObject(object).getSize(new Vector3()).x;
}
